import { useEffect, useMemo, useState } from "react";
import {
  botQuestions,
  getCommunicationQuestion,
  getPlatformKey,
  getVerificationQuestion,
  validateByStep,
} from "./chatFlow";

const makeKey = (base, name) => `${base}:${name}`;

function usePersistedState(key, initialValue) {
  const [state, setState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch {}
  }, [key, state]);

  return [state, setState];
}

export function useChatFlow({
  sceneActive,
  playScene,
  storageKey = "chat_flow_state_v1",
}) {
  const [input, setInput] = usePersistedState(makeKey(storageKey, "input"), "");
  const [step, setStep] = usePersistedState(makeKey(storageKey, "step"), 0);
  const [messages, setMessages] = usePersistedState(
    makeKey(storageKey, "messages"),
    [],
  );

  const [editingId, setEditingId] = usePersistedState(
    makeKey(storageKey, "editingId"),
    null,
  );
  const [editingText, setEditingText] = usePersistedState(
    makeKey(storageKey, "editingText"),
    "",
  );

  const [verificationMethod, setVerificationMethod] = usePersistedState(
    makeKey(storageKey, "verificationMethod"),
    "",
  );
  const [verificationContact, setVerificationContact] = usePersistedState(
    makeKey(storageKey, "verificationContact"),
    "",
  );
  const [userEmail, setUserEmail] = usePersistedState(
    makeKey(storageKey, "userEmail"),
    "",
  );
  const [communicationPlatform, setCommunicationPlatform] = usePersistedState(
    makeKey(storageKey, "communicationPlatform"),
    "",
  );
  const [useSameAccountChoice, setUseSameAccountChoice] = usePersistedState(
    makeKey(storageKey, "useSameAccountChoice"),
    "",
  );
  const [communicationContact, setCommunicationContact] = usePersistedState(
    makeKey(storageKey, "communicationContact"),
    "",
  );
  const [selectedNumbersMode, setSelectedNumbersMode] = usePersistedState(
    makeKey(storageKey, "selectedNumbersMode"),
    "",
  );

  const isRadioStep = step === 1 || step === 5 || step === 6 || step === 9;
  const isUploadStep = step === 10;

  const [uploading, setUploading] = usePersistedState(
    makeKey(storageKey, "uploading"),
    false,
  );
  const [uploadedDoc, setUploadedDoc] = usePersistedState(
    makeKey(storageKey, "uploadedDoc"),
    null,
  );
  const [numbersMessageSent, setNumbersMessageSent] = usePersistedState(
    makeKey(storageKey, "numbersMessageSent"),
    false,
  );

  const [countriesModalOpen, setCountriesModalOpen] = usePersistedState(
    makeKey(storageKey, "countriesModalOpen"),
    false,
  );
  const [selectedCountry, setSelectedCountry] = usePersistedState(
    makeKey(storageKey, "selectedCountry"),
    null,
  );

  useEffect(() => {
    if (messages.length) return;
    const t = setTimeout(() => {
      setMessages([
        { id: "bot-0", from: "bot", stepIndex: 0, ...botQuestions[0] },
      ]);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  const startEdit = (msg) => {
    if (msg.from !== "user") return;
    setEditingId(msg.id);
    setEditingText(msg.text || "");
  };

  const closeEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const closeCountriesModal = () => setCountriesModalOpen(false);

  const pickCountry = (country) => {
    setSelectedCountry(country);
    setCountriesModalOpen(false);
    runSceneAndAsk(11, 9);
  };

  const validate = (text, stepIndex) =>
    validateByStep(text, stepIndex, {
      verificationMethod,
      communicationPlatform,
    });

  const inputTrimmed = input.trim();

  const hasUserAnswerForStep = useMemo(
    () => messages.some((m) => m.from === "user" && m.stepIndex === step),
    [messages, step],
  );

  const uploadDocument = async (file) => {
    if (sceneActive || editingId || uploading) return;

    setUploadedDoc(file);

    try {
      setUploading(true);
      if (!numbersMessageSent) {
        setNumbersMessageSent(true);
        runSceneAndAsk(11, 10);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  const hasInvalidForStep = useMemo(
    () =>
      messages.some(
        (m) => m.from === "user" && m.stepIndex === step && m.invalid,
      ),
    [messages, step],
  );

  const canSend =
    !editingId &&
    !sceneActive &&
    !isRadioStep &&
    !isUploadStep &&
    inputTrimmed.length > 0 &&
    !hasUserAnswerForStep &&
    !hasInvalidForStep;

  const askNextQuestionNow = (nextStep, override = null) => {
    const q = botQuestions[nextStep];

    setMessages((prev) => {
      if (q.type === "multi") {
        const now = Date.now();
        return [
          ...prev,
          ...q.parts.map((p, i) => ({
            id: `bot-${now}-${nextStep}-${i}`,
            from: "bot",
            stepIndex: nextStep,
            ...p,
          })),
        ];
      }

      const msg = {
        id: `bot-${Date.now()}-${nextStep}`,
        from: "bot",
        stepIndex: nextStep,
        ...q,
        ...(override || {}),
      };

      return [...prev, msg];
    });

    setStep(nextStep);
  };

  const runSceneAndAsk = (nextStep, stepIndexJustAnswered) => {
    const persistOverlay =
      stepIndexJustAnswered === 0 || stepIndexJustAnswered === 1;

    const revealPhone = stepIndexJustAnswered === 2;
    const revealAuth = stepIndexJustAnswered === 3;

    const revealEmail =
      (stepIndexJustAnswered === 3 && verificationMethod === "Email") ||
      (stepIndexJustAnswered === 4 && verificationMethod !== "Email");

    const revealPlatformForm =
      stepIndexJustAnswered === 7 ||
      (stepIndexJustAnswered === 6 && useSameAccountChoice === "Mentioned");

    const platformFormKey = revealPlatformForm
      ? getPlatformKey(communicationPlatform || "Telegram")
      : "";

    const revealCompanyScene = stepIndexJustAnswered === 8;
    const companySceneKey = getPlatformKey(communicationPlatform || "Telegram");

    playScene?.(() => askNextQuestionNow(nextStep), {
      persistOverlay,
      revealPhone,
      revealAuth,
      revealEmail,
      revealPlatformForm,
      platformFormKey,
      revealCompanyScene,
      companySceneKey,
    });
  };

  const send = () => {
    if (!canSend) return;

    const text = input.trim();
    const id = `user-${Date.now()}`;
    const stepIndex = step;

    setMessages((prev) => [
      ...prev,
      { id, from: "user", text, stepIndex, invalid: false, error: "" },
    ]);
    setInput("");

    const err = validate(text, stepIndex);
    if (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === id ? { ...m, invalid: true, error: err } : m,
        ),
      );
      return;
    }

    if (stepIndex === 2) setVerificationContact(text);
    if (stepIndex === 4) setUserEmail(text);
    if (stepIndex === 7) setCommunicationContact(text);

    if (stepIndex === 3) {
      const next = verificationMethod === "Email" ? 5 : 4;
      runSceneAndAsk(next, stepIndex);
      return;
    }

    if (stepIndex === 4) {
      runSceneAndAsk(5, stepIndex);
      return;
    }

    if (stepIndex === 7) {
      runSceneAndAsk(8, stepIndex);
      return;
    }

    if (step + 1 < botQuestions.length) {
      runSceneAndAsk(step + 1, stepIndex);
    }
  };

  const selectVerificationOption = (optionText) => {
    if (sceneActive) return;
    if (editingId) return;
    if (step !== 1) return;

    setVerificationMethod(optionText);
    const question = getVerificationQuestion(optionText);

    setMessages((prev) => {
      const withoutOld2 = prev.filter((x) => x.stepIndex !== 2);
      return [
        ...withoutOld2,
        {
          id: `bot-${Date.now()}-2`,
          from: "bot",
          stepIndex: 2,
          type: "text",
          text: question,
        },
      ];
    });

    setStep(2);
  };

  const selectPlatformOption = (optionText) => {
    if (sceneActive) return;
    if (editingId) return;
    if (step !== 5) return;

    setCommunicationPlatform(optionText);
    setUseSameAccountChoice("");

    const shouldAskSameAccount =
      optionText === "Email" ||
      (optionText === "Telegram" && verificationMethod === "Telegram");

    if (shouldAskSameAccount) {
      const txt =
        optionText === "Telegram"
          ? "Do you want to use the Telegram mentioned earlier or another one?"
          : "Do you want to use the Email mentioned earlier or another one?";

      askNextQuestionNow(6, { text: txt });
      return;
    }

    const q = getCommunicationQuestion(optionText);
    askNextQuestionNow(7, { type: "text", text: q });
  };

  const selectSameAccountOption = (optionText) => {
    if (sceneActive) return;
    if (editingId) return;
    if (step !== 6) return;

    setUseSameAccountChoice(optionText);

    if (optionText === "Mentioned") {
      if (communicationPlatform === "Email") {
        const mentionedEmail =
          userEmail ||
          (verificationMethod === "Email" ? verificationContact : "");
        setCommunicationContact(mentionedEmail);
      } else if (communicationPlatform === "Telegram") {
        setCommunicationContact(verificationContact);
      }

      const platformFormKey = getPlatformKey(
        communicationPlatform || "Telegram",
      );

      playScene?.(() => askNextQuestionNow(8), {
        persistOverlay: true,
        revealPlatformForm: true,
        platformFormKey,
      });

      return;
    }

    const q = getCommunicationQuestion(communicationPlatform);
    askNextQuestionNow(7, { type: "text", text: q });
  };

  const selectNumbersOption = (optionText) => {
    if (sceneActive) return;
    if (editingId) return;
    if (step !== 9) return;

    setSelectedNumbersMode(optionText);

    if (optionText === "Use your own") {
      setUploadedDoc(null);
      setNumbersMessageSent(false);
      runSceneAndAsk(10, 9);
      return;
    }

    setCountriesModalOpen(true);
  };

  const saveEdit = () => {
    const text = editingText.trim();
    if (!text) return;

    const current = messages.find((m) => m.id === editingId);
    if (!current) return;

    const stepIndex = current.stepIndex ?? 0;
    const err = validate(text, stepIndex);

    setMessages((prev) =>
      prev.map((m) =>
        m.id === editingId
          ? { ...m, text, invalid: !!err, error: err || "" }
          : m,
      ),
    );

    closeEdit();

    if (!err && stepIndex === step) {
      if (stepIndex === 3) {
        const next = verificationMethod === "Email" ? 5 : 4;
        runSceneAndAsk(next, stepIndex);
        return;
      }

      if (stepIndex === 4) {
        runSceneAndAsk(5, stepIndex);
        return;
      }

      if (stepIndex === 7) {
        runSceneAndAsk(8, stepIndex);
        return;
      }

      if (step + 1 < botQuestions.length) {
        const nextStep = step + 1;
        const alreadyAskedNext = messages.some(
          (m) => m.from === "bot" && m.stepIndex === nextStep,
        );
        if (!alreadyAskedNext) runSceneAndAsk(nextStep, stepIndex);
      }
    }
  };

  return {
    input,
    setInput,
    step,
    messages,

    editingId,
    editingText,
    setEditingText,

    verificationMethod,
    selectedNumbersMode,
    communicationPlatform,

    isRadioStep,
    canSend,

    startEdit,
    send,
    saveEdit,

    selectVerificationOption,
    selectPlatformOption,
    selectSameAccountOption,
    selectNumbersOption,
    useSameAccountChoice,
    uploadedDoc,
    uploading,
    uploadDocument,
    countriesModalOpen,
    closeCountriesModal,
    pickCountry,
    selectedCountry,
  };
}
